import { Box, Button, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AppModal from "global/components/AppModal/AppModal";
import strings from "global/constants/StringConstants";
import { fillCopy } from "helpers/methods";
import {
  PromoCampaign,
  promoDiscounts,
  promoEndShort,
} from "helpers/promotionHelpers";
import { promoThemes, promoTokens } from "./DashboardPromotions.tokens";

interface Props {
  open: boolean;
  campaign: PromoCampaign;
  finalDays: boolean;
  onClaim: () => void;
  onClose: () => void;
}

const t = promoTokens.popup;
const copy = strings.promotions.popup;

/** Floating document card on the hero — decoration only. */
const HeroCard = ({ sx }: { sx: object }) => (
  <Box
    aria-hidden="true"
    sx={{
      position: "absolute",
      boxSizing: "border-box",
      background: t.card.bg,
      border: t.card.border,
      borderRadius: "10px",
      padding: "12px",
      display: "flex",
      flexDirection: "column",
      gap: "6px",
      ...sx,
    }}
  >
    <Box sx={{ height: 5, width: "60%", borderRadius: "3px", background: t.card.lineStrong }} />
    <Box sx={{ height: 4, width: "85%", borderRadius: "2px", background: t.card.line }} />
    <Box sx={{ height: 4, width: "70%", borderRadius: "2px", background: t.card.line }} />
    <Box sx={{ flex: 1 }} />
    <Box component="svg" viewBox="0 0 60 18" fill="none" sx={{ width: "70%", height: "auto" }}>
      <path
        d="M2 13c5-8 9-9 10-5s-3 7 0 6 6-10 9-10-1 9 2 9 5-6 8-6 0 5 3 5 7-3 11-4 7 1 13 0"
        stroke={t.card.squiggle}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </Box>
  </Box>
);

/**
 * Once-per-campaign promotion popup on the dashboard. Built on the shared
 * `AppModal` shell (focus trap, Esc, scrim, scroll lock) with its own
 * two-pane body: the discount hero on the left, the offer on the right.
 * Stays a centred card on phones — the design stacks the panes rather than
 * dropping to a bottom sheet.
 */
const DashboardPromoPopup = ({ open, campaign, finalDays, onClaim, onClose }: Props) => {
  const muiTheme = useTheme();
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up("md"));
  const theme = promoThemes[campaign];
  const discount = promoDiscounts[campaign];
  const campaignCopy = copy[campaign];
  const badge = fillCopy(finalDays ? campaignCopy.finalBadge : campaignCopy.badge, {
    endShort: promoEndShort(campaign),
  });
  const rows = [
    { pct: discount.annual, what: copy.annualWhat, how: copy.annualHow },
    { pct: discount.monthly, what: copy.monthlyWhat, how: copy.monthlyHow },
  ];

  return (
    <AppModal
      open={open}
      onClose={onClose}
      ariaLabel={campaignCopy.title}
      hideHeader
      hideClose
      hideFooter
      disableSheet
      disableBodyPadding
      scrollBody={false}
      width={t.width}
      paperSx={{
        borderRadius: t.radius,
        boxShadow: t.shadow,
        overflow: "hidden",
        border: "none",
      }}
      bodySx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        overflowY: { xs: "auto", md: "hidden" },
      }}
    >
      {/* Hero: the figures are restated in the offer rows, so it is hidden
          from assistive tech rather than read twice. */}
      <Box
        aria-hidden="true"
        sx={{
          position: "relative",
          flex: { xs: "0 0 auto", md: `0 0 ${t.heroBasis}` },
          boxSizing: "border-box",
          background: theme.heroBg,
          color: "common.white",
          padding: t.heroPad,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {isDesktop && (
          <>
            <HeroCard sx={{ top: 34, right: 46, width: 104, height: 132, transform: "rotate(9deg)" }} />
            <HeroCard sx={{ bottom: -30, left: -24, width: 96, height: 120, transform: "rotate(-12deg)" }} />
            <HeroCard sx={{ bottom: 92, right: -20, width: 84, height: 104, transform: "rotate(-6deg)" }} />
          </>
        )}
        <Typography
          sx={{ position: "relative", fontSize: "0.75rem", fontWeight: 800, letterSpacing: "0.14em", color: t.heroEyebrow }}
        >
          {copy.saveUpTo}
        </Typography>
        <Typography
          sx={{
            position: "relative",
            fontSize: t.heroNum,
            fontWeight: 800,
            letterSpacing: "-0.05em",
            lineHeight: 0.95,
            color: theme.heroAccent,
            mt: "6px",
          }}
        >
          {`${discount.annual}%`}
        </Typography>
        <Typography
          sx={{ position: "relative", fontSize: t.heroLine, fontWeight: 700, color: "common.white", mt: "10px" }}
        >
          {copy.heroAnnual}
        </Typography>
        <Box sx={{ position: "relative", height: "1px", background: t.heroRule, margin: t.heroRuleMargin }} />
        <Box sx={{ position: "relative", display: "flex", alignItems: "baseline", flexWrap: "wrap", gap: "10px" }}>
          <Typography
            component="span"
            sx={{ fontSize: t.heroNum2, fontWeight: 800, letterSpacing: "-0.04em", lineHeight: 1, color: theme.heroAccent }}
          >
            {`${discount.monthly}%`}
          </Typography>
          <Typography component="span" sx={{ fontSize: "0.875rem", fontWeight: 600, color: "common.white" }}>
            {copy.heroMonthly}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          position: "relative",
          flex: 1,
          minWidth: 0,
          boxSizing: "border-box",
          overflowY: { xs: "visible", md: "auto" },
          padding: t.bodyPad,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <IconButton
          aria-label={copy.close}
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            width: 38,
            height: 38,
            border: `1px solid ${t.border}`,
            borderRadius: "50%",
            backgroundColor: "common.white",
            color: t.ink,
            "&:hover": { backgroundColor: t.closeHoverBg },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <Typography
          component="span"
          sx={{
            alignSelf: "flex-start",
            mr: "44px",
            fontSize: "0.6875rem",
            fontWeight: 800,
            letterSpacing: "0.08em",
            lineHeight: 1.2,
            color: theme.heroAccent,
            backgroundColor: t.ink,
            borderRadius: "999px",
            padding: "7px 14px",
          }}
        >
          {badge}
        </Typography>
        <Typography
          component="h2"
          sx={{
            m: "18px 0 0",
            fontSize: t.titleSize,
            fontWeight: 800,
            lineHeight: 1.12,
            letterSpacing: "-0.025em",
            color: t.ink,
          }}
        >
          {campaignCopy.title}
        </Typography>
        <Typography sx={{ mt: "12px", fontSize: "0.90625rem", lineHeight: 1.6, color: t.body }}>
          {fillCopy(campaignCopy.sub, { annual: discount.annual })}
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", mt: "22px" }}>
          {rows.map((row) => (
            <Box
              key={row.what}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                padding: "14px 16px",
                borderRadius: "14px",
                border: `1px solid ${t.border}`,
              }}
            >
              <Typography
                component="span"
                sx={{
                  flexShrink: 0,
                  minWidth: 92,
                  boxSizing: "border-box",
                  textAlign: "center",
                  fontSize: "1.0625rem",
                  fontWeight: 800,
                  color: t.ink,
                  backgroundColor: theme.chipBg,
                  borderRadius: "9px",
                  padding: "9px 12px",
                }}
              >
                {fillCopy(copy.percentOff, { pct: row.pct })}
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
                <Typography sx={{ fontSize: "0.9375rem", fontWeight: 700, color: t.ink }}>{row.what}</Typography>
                <Typography sx={{ fontSize: "0.8125rem", color: t.meta }}>{row.how}</Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: "9px", mt: "18px", fontSize: "0.8125rem", color: t.secondary }}>
          <Box
            component="svg"
            aria-hidden="true"
            viewBox="0 0 24 24"
            fill="none"
            stroke={t.check}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            sx={{ width: 17, height: 17, flexShrink: 0 }}
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M8.5 12.2l2.4 2.4 4.6-4.8" />
          </Box>
          {copy.autoApplied}
        </Box>

        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={onClaim}
          endIcon={<ArrowForwardRoundedIcon />}
          sx={{ mt: "22px" }}
        >
          {copy.claim}
        </Button>
        <Button
          variant="text"
          onClick={onClose}
          sx={{ alignSelf: "center", mt: "10px", color: t.meta, "&:hover": { color: t.ink } }}
        >
          {copy.notNow}
        </Button>
      </Box>
    </AppModal>
  );
};

export default DashboardPromoPopup;
